import axios from 'axios';
import { getJson, sendJson } from '@/lib/api';
import { authHeader } from '@/lib/events';

/** What can change on an event; mirrors the API's NOTIFICATION_KIND. */
export const NOTIFICATION_KINDS = [
  'note.created',
  'note.updated',
  'shipment.created',
  'shipment.updated',
  'shipment.return-label-unused',
  'event.date',
  'event.venue',
  'event.facilitator',
  'event.users',
  'message.mention',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const NOTIFICATION_KIND_LABELS: Readonly<Record<NotificationKind, string>> = {
  'note.created': 'A note is added',
  'note.updated': 'A note is edited',
  'shipment.created': 'A shipment is added',
  'shipment.updated': 'A shipment changes',
  'shipment.return-label-unused': 'A return label goes unused',
  'event.date': 'The date changes',
  'event.venue': 'The venue changes',
  'event.facilitator': 'The facilitator changes',
  'event.users': 'The team changes',
  'message.mention': 'A client tags you or replies to you',
};

/** What each kind is about, one line, for the preferences table. */
export const NOTIFICATION_KIND_HINTS: Readonly<Record<NotificationKind, string>> = {
  'note.created': 'On an event you are on, within three months of its date',
  'note.updated': 'Repeated edits within a minute count once',
  'shipment.created': 'Carrier, tracking and items',
  'shipment.updated': 'Any change to a shipment on your event',
  'shipment.return-label-unused': 'No carrier scan a week after the event; sent to the production contact',
  'event.date': 'When Salesforce moves the date',
  'event.venue': 'When Salesforce moves the venue',
  'event.facilitator': 'When the lead facilitator changes in Salesforce',
  'event.users': 'When someone joins or leaves the crew',
  'message.mention': 'On the event thread in their portal; the link opens it with your staff sign-in',
};

/** The kinds, grouped the way the preferences table lists them. */
export const NOTIFICATION_KIND_GROUPS: readonly { readonly label: string; readonly kinds: readonly NotificationKind[] }[] = [
  { label: 'Notes', kinds: ['note.created', 'note.updated'] },
  { label: 'Shipments', kinds: ['shipment.created', 'shipment.updated', 'shipment.return-label-unused'] },
  { label: 'The event', kinds: ['event.date', 'event.venue', 'event.facilitator', 'event.users'] },
  { label: 'Client portal', kinds: ['message.mention'] },
];

/** Where one kind of change reaches you. */
export interface KindChannels {
  readonly inApp: boolean;
  readonly email: boolean;
}
export const DEFAULT_KIND_CHANNELS: KindChannels = { inApp: true, email: true };

export interface Notification {
  readonly _id: string;
  /** What it is about: the collection and the row, e.g. event / booking_request. */
  readonly relatedToType: string;
  readonly relatedTo: string;
  readonly kind: NotificationKind;
  readonly title: string;
  readonly body?: string;
  readonly href?: string;
  readonly readAt?: string;
  readonly emailedAt?: string;
  readonly createdAt: string;
}

export interface NotificationPreference {
  readonly userId: string;
  /** Per kind; a kind not listed is on both ways. */
  readonly kinds: Partial<Record<NotificationKind, KindChannels>>;
  /** Per subject, keyed `<type>:<id>`, e.g. `event:66f1…`. */
  readonly overrides: Readonly<Record<string, { readonly muted: boolean; readonly kinds?: readonly NotificationKind[] }>>;
}

/** The override key for one subject. */
export function subjectKey(type: string, id: string): string {
  return `${type}:${id}`;
}

/** Where a notification's subject lives in this app, when the row carries no link of its own. */
export function subjectHref(notification: Pick<Notification, 'relatedToType' | 'relatedTo'>): string {
  switch (notification.relatedToType) {
    case 'event':
      return `/events/${notification.relatedTo}`;
    case 'shipment':
      return `/shipments/${notification.relatedTo}`;
    case 'organization':
      return `/organizations/${notification.relatedTo}`;
    default:
      // A subject with no page of its own here: the feed is the best landing.
      return '/notifications';
  }
}

export function channelsFor(preference: NotificationPreference, kind: NotificationKind): KindChannels {
  return preference.kinds[kind] ?? DEFAULT_KIND_CHANNELS;
}

/** The caller's feed plus the unread count. */
export async function listNotifications(options: { unread?: boolean; limit?: number } = {}, signal?: AbortSignal): Promise<{ rows: readonly Notification[]; unread: number }> {
  const params: Record<string, string | number> = { limit: options.limit ?? 50 };
  if (options.unread) params['unread'] = 'true';
  const response = await axios.get<{ data: Notification[]; meta?: { unread?: number } }>('/api/v3/notifications', { params, ...(signal ? { signal } : {}), headers: await authHeader() });
  return { rows: response.data.data, unread: response.data.meta?.unread ?? 0 };
}

export async function markNotificationRead(id: string): Promise<void> {
  await sendJson('post', `/notifications/${id}/read`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await sendJson('post', '/notifications/read-all');
}

export async function readNotificationPreferences(signal?: AbortSignal): Promise<NotificationPreference> {
  return getJson<NotificationPreference>('/me/notification-preferences', undefined, signal);
}

/** Sets the channels for the kinds given; kinds left out keep their setting. */
export async function saveNotificationPreferences(kinds: Partial<Record<NotificationKind, KindChannels>>): Promise<NotificationPreference> {
  return sendJson<NotificationPreference>('put', '/me/notification-preferences', { kinds });
}

/** A link into the client's portal is absolute; everything else is a route in this app. */
export function isExternalHref(href: string | undefined): boolean {
  return href !== undefined && /^https?:\/\//.test(href);
}

/** Follow or mute one event; null returns it to the global preference. */
/** Follows or mutes one subject of any type; null clears the override. */
export async function saveSubjectNotificationOverride(
  type: string,
  id: string,
  override: { muted: boolean } | null,
): Promise<NotificationPreference> {
  return sendJson<NotificationPreference>('put', `/notification-preferences/subjects/${type}/${id}`, { override });
}

/** "2 minutes ago", "Yesterday", or a date. */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const diff = now - Date.parse(iso);
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
